import {
  useGatewayLazyQuery,
  useGatewayMutation,
} from "@clockwise/web-commons/src/network/apollo/gateway-provider";
import { TrackingEvents, useTracking } from "@clockwise/web-commons/src/util/analytics.util";
import { isEmpty, isEqual } from "lodash";
import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import {
  useReadShowMemoryModal,
  useUpdateShowMemoryModal,
} from "../chat-plus-calendar/util/MemoryModalContext";
import { useSetAIError } from "../chat/ai-chat/hooks/AIErrorContext";
import { AIMemoryModal } from "./AIMemoryModal";
import { AiMemoryDocument, UpdateAiMemoryDocument } from "./__generated__/Memory.v2.generated";

export const AIMemoryModalWrapper = () => {
  const track = useTracking();
  const showMemoryModal = useReadShowMemoryModal();
  const updateShowMemoryModal = useUpdateShowMemoryModal();
  const [memoryList, setMemoryList] = useState<string[]>([]);
  const [originalMemoryList, setOriginalMemoryList] = useState<string[]>([]);
  const [getMemory, { data, loading }] = useGatewayLazyQuery(AiMemoryDocument);
  const [updateAIMemory] = useGatewayMutation(UpdateAiMemoryDocument);
  const setError = useSetAIError();

  useEffect(() => {
    if (data && data.aiMemory) {
      setMemoryList(data.aiMemory);
      setOriginalMemoryList(data.aiMemory);
    }
  }, [data]);

  useEffect(() => {
    if (showMemoryModal) {
      void getMemory({ fetchPolicy: "network-only" });
    }
  }, [showMemoryModal]);

  const onSaveNewMemory = useCallback(
    (input: string[]) => {
      void updateAIMemory({
        variables: { input: input },
        onCompleted: () => {
          updateShowMemoryModal(false);
          toast.success("Memory updated");
          track(TrackingEvents.CHAT.MEMORY.MEMORY_UPDATED_SUCCESS, {
            oldMemory: originalMemoryList,
            newMemory: input,
          });
        },
        onError: (error) => {
          track(TrackingEvents.CHAT.MEMORY.MEMORY_UPDATED_FAILED);
          setError({
            error,
            message: "Failed update memory",
            showUserMessage: true,
          });
        },
      });
    },
    [memoryList],
  );

  const onAddMemory = useCallback(
    (text: string) => {
      const newMemories = [...memoryList].concat(text);
      setMemoryList(newMemories);
    },
    [memoryList],
  );

  const onDeleteMemory = useCallback(
    (index: number) => {
      const newMemories = [...memoryList].filter((_memory, indexMemory) => index !== indexMemory);
      setMemoryList(newMemories);
    },
    [memoryList],
  );

  const onSave = useCallback(
    (inputs?: string[]) => {
      if (!!inputs) {
        onSaveNewMemory(inputs.concat(memoryList));
      } else {
        onSaveNewMemory(memoryList);
      }
    },
    [memoryList],
  );

  const onClose = useCallback(() => {
    updateShowMemoryModal(false);
  }, [memoryList]);

  const isDisabled = isEqual(originalMemoryList, memoryList);
  const fetchedMemoriesEmpty = !loading && isEmpty(originalMemoryList);
  return (
    <AIMemoryModal
      memoryList={memoryList}
      onAdd={onAddMemory}
      onDelete={onDeleteMemory}
      onSave={onSave}
      onClose={onClose}
      isOpen={showMemoryModal}
      loading={loading}
      disabled={isDisabled}
      fetchedMemoriesEmpty={fetchedMemoriesEmpty}
    />
  );
};
